/* ============================================================
   THAHAB — Shopify Theme JavaScript
   GSAP animations · Swiper sliders · Nav scroll · Hover effects
   ============================================================ */

(function () {
  'use strict';

  /* ── Utility: reduced motion ── */
  const prefersReduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const themeStrings = window.__thahab_strings || {};

  /* ────────────────────────────────────────────────
     NAVIGATION — transparent → solid on scroll
  ──────────────────────────────────────────────── */
  function initNav() {
    const nav = document.querySelector('.site-nav');
    if (!nav) return;

    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    const isHome = document.body.dataset.template === 'index';
    let raf = 0;

    function updateNav() {
      const scrolled = window.scrollY > 10;
      nav.classList.toggle('is-solid', scrolled || !isHome);
      nav.classList.toggle('is-expanded', isHome && !scrolled);
      if (headerGroup) headerGroup.classList.toggle('is-header-solid', scrolled);
    }

    function onScroll() {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(updateNav);
    }

    updateNav();
    window.addEventListener('scroll', onScroll, { passive: true });

    /* Hamburger */
    const hamburger = nav.querySelector('.site-nav__hamburger');
    const mobilePanel = nav.querySelector('.site-nav__mobile');

    if (hamburger && mobilePanel) {
      hamburger.addEventListener('click', () => {
        const open = hamburger.classList.toggle('is-open');
        mobilePanel.classList.toggle('is-open', open);
        nav.classList.toggle('is-solid', true);
        hamburger.setAttribute('aria-expanded', String(open));
        hamburger.setAttribute('aria-label', open ? hamburger.dataset.closeLabel : hamburger.dataset.openLabel);
        document.body.style.overflow = open ? 'hidden' : '';
      });

      // Close on link click
      mobilePanel.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          hamburger.classList.remove('is-open');
          mobilePanel.classList.remove('is-open');
          hamburger.setAttribute('aria-expanded', 'false');
          hamburger.setAttribute('aria-label', hamburger.dataset.openLabel || 'Open menu');
          document.body.style.overflow = '';
        });
      });
    }
  }

  /* ────────────────────────────────────────────────
     SCROLL REVEAL — Intersection Observer
  ──────────────────────────────────────────────── */
  function initScrollReveal() {
    if (prefersReduced()) {
      document.querySelectorAll('.reveal, .reveal-scale, .reveal-left').forEach((el) => {
        el.classList.add('is-visible');
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal, .reveal-scale, .reveal-left').forEach((el) => {
      // These components get a single GSAP entrance. Keeping their wrapper
      // visible prevents the CSS and GSAP reveal systems from fighting.
      if (el.matches('.section-header, .gallery-bento__item, .split-section__image, .split-section__text, .image-text__image, .image-text__text')) {
        el.classList.add('is-visible');
        return;
      }
      observer.observe(el);
    });
  }

  /* ────────────────────────────────────────────────
     GSAP ANIMATIONS (loaded from CDN via layout)
  ──────────────────────────────────────────────── */
  function initGSAP() {
    if (typeof gsap === 'undefined' || prefersReduced()) return;

    const { ScrollTrigger } = window;
    if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    const isRTL = document.documentElement.dir === 'rtl';
    const compactMotion = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
    const motionDistance = compactMotion ? 24 : 48;
    const inlineRevealClip = isRTL ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)';

    /* Hero — background zoom on scroll */
    const heroBg = document.querySelector('.hero__bg');
    if (heroBg && ScrollTrigger) {
      gsap.to(heroBg, {
        scale: 1.12,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        },
      });
    }

    /* Hero — content fade out on scroll */
    const heroContent = document.querySelector('.hero__content-wrap');
    if (heroContent && ScrollTrigger) {
      gsap.to(heroContent, {
        opacity: 0,
        y: -60,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: '30% top',
          end: 'bottom top',
          scrub: 1,
        },
      });
    }

    /* Hero — entry sequence */
    const heroLabel = document.querySelector('.hero__label');
    const heroTitle = document.querySelector('.hero__title');
    const heroSub   = document.querySelector('.hero__subtitle');
    const heroActions = document.querySelector('.hero__actions');
    const heroScroll = document.querySelector('.hero__scroll');

    if (heroLabel && heroTitle) {
      const tl = gsap.timeline({ delay: 0.25 });
      tl.from(heroLabel, { opacity: 0, y: 20, duration: 0.7, ease: 'power3.out' })
        .from(heroTitle,   { opacity: 0, y: 50, duration: 1.1, ease: 'power3.out' }, '-=0.4')
        .from(heroSub,     { opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' }, '-=0.5')
        .from(heroActions?.children || [], {
          opacity: 0, y: 20, scale: 0.94, duration: 0.7, stagger: 0.12,
          ease: 'back.out(1.7)'
        }, '-=0.4')
        .from(heroScroll,  { opacity: 0, duration: 1, ease: 'power2.out' }, '-=0.2');
    }

    /* About page hero — one calm above-the-fold entrance. */
    const aboutContent = document.querySelector('.about-hero__content');
    if (aboutContent) {
      gsap.from(Array.from(aboutContent.children), {
        opacity: 0,
        y: compactMotion ? 18 : 32,
        duration: compactMotion ? 0.65 : 0.9,
        stagger: 0.1,
        delay: 0.15,
        ease: 'power3.out',
      });
    }

    /* Parallax sections */
    document.querySelectorAll('.parallax-section__bg').forEach((bg) => {
      if (!ScrollTrigger) return;
      gsap.to(bg, {
        yPercent: 25,
        ease: 'none',
        scrollTrigger: {
          trigger: bg.closest('.parallax-section'),
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      });
    });

    /* CTA — sand parallax */
    const ctaSand = document.querySelector('.cta-section__sand');
    if (ctaSand && ScrollTrigger) {
      gsap.to(ctaSand, {
        y: -30,
        ease: 'none',
        scrollTrigger: {
          trigger: ctaSand.closest('.cta-section'),
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      });
    }

    /* CTA — title clip reveal */
    const ctaTitle = document.querySelector('.cta-section__title');
    if (ctaTitle && ScrollTrigger) {
      gsap.set(ctaTitle, { clipPath: inlineRevealClip });
      gsap.to(ctaTitle, {
        clipPath: 'inset(0% 0% 0% 0%)',
        opacity: 1,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: ctaTitle,
          start: 'top 80%',
        },
      });
    }

    /* Stats stamp */
    const stamp = document.querySelector('.stats-stamp');
    if (stamp && ScrollTrigger) {
      gsap.from(stamp, {
        opacity: 0,
        scale: 0.88,
        rotate: -3,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: { trigger: stamp, start: 'top 75%' },
      });
    }

    /* Stats cards */
    document.querySelectorAll('.stats-card').forEach((card, i) => {
      if (!ScrollTrigger) return;
      gsap.from(card, {
        opacity: 0,
        x: isRTL ? -30 : 30,
        duration: 0.8,
        delay: i * 0.1,
        ease: 'power3.out',
        scrollTrigger: { trigger: card, start: 'top 85%' },
      });
    });

    /* Section headers — stagger children */
    document.querySelectorAll('.section-header, .rich-text').forEach((header) => {
      if (!ScrollTrigger) return;
      gsap.from(Array.from(header.children), {
        opacity: 0,
        y: 40,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: { trigger: header, start: 'top 80%' },
      });
    });

    /* Product showcase */
    const showcase = document.querySelector('.product-showcase');
    if (showcase && ScrollTrigger) {
      gsap.from(showcase, {
        opacity: 0,
        y: 60,
        duration: 1.1,
        ease: 'power3.out',
        scrollTrigger: { trigger: showcase, start: 'top 80%' },
      });
    }

    /* ── Generic stagger helper ── */
    function staggerIn(selector, vars, triggerEl) {
      const els = document.querySelectorAll(selector);
      if (!els.length || !ScrollTrigger) return;
      gsap.from(els, {
        opacity: 0, y: motionDistance, scale: 0.96,
        duration: 0.85, stagger: 0.1, ease: 'power3.out',
        ...vars,
        scrollTrigger: {
          trigger: triggerEl || els[0],
          start: 'top 85%',
        },
      });
    }

    /* ── Product cards — stagger per row ── */
    document.querySelectorAll('.products-grid').forEach((grid) => {
      if (!ScrollTrigger) return;
      gsap.from(grid.querySelectorAll('.product-card'), {
        opacity: 0, y: compactMotion ? 28 : 60, scale: compactMotion ? 0.98 : 0.94,
        duration: 0.9, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: grid, start: 'top 85%' },
      });
    });

    /* ── Gallery bento items ── */
    document.querySelectorAll('.gallery-bento').forEach((grid) => {
      if (!ScrollTrigger) return;
      gsap.from(grid.querySelectorAll('.gallery-bento__item'), {
        opacity: 0, y: motionDistance, scale: compactMotion ? 0.98 : 0.93,
        duration: 1, stagger: 0.08, ease: 'power3.out',
        scrollTrigger: { trigger: grid, start: 'top 80%' },
      });
    });

    /* ── Experience / occasion cards ── */
    document.querySelectorAll('.experience-section__grid').forEach((grid) => {
      if (!ScrollTrigger) return;
      gsap.from(grid.querySelectorAll('.experience-card, [class*="occasion"]'), {
        opacity: 0, y: 60, scale: 0.93,
        duration: 1, stagger: 0.12, ease: 'power3.out',
        scrollTrigger: { trigger: grid, start: 'top 80%' },
      });
    });

    /* ── Collection category cards ── */
    staggerIn('.category-card', { y: 60, scale: 0.93, duration: 0.9, stagger: 0.1 });

    /* ── Coming-soon cards ── */
    staggerIn('.coming-soon-card', { y: 40, scale: 0.95, duration: 0.8, stagger: 0.1 });

    /* ── Portfolio items ── */
    document.querySelectorAll('.portfolio-item').forEach((item, i) => {
      if (!ScrollTrigger) return;
      gsap.from(item, {
        opacity: 0, scale: 0.9,
        duration: 0.8, delay: i * 0.08, ease: 'power3.out',
        scrollTrigger: { trigger: item, start: 'top 88%' },
      });
    });

    /* ── Split sections — image clips in from side ── */
    document.querySelectorAll('.split-section__image, .image-text__image').forEach((img, i) => {
      if (!ScrollTrigger) return;
      const fromRight = i % 2 !== 0;
      const horizontalOffset = (fromRight ? 1 : -1) * (isRTL ? -1 : 1) * (compactMotion ? 24 : 60);
      gsap.from(img, {
        opacity: 0, x: horizontalOffset, scale: compactMotion ? 0.98 : 0.96,
        duration: 1.1, ease: 'power3.out',
        scrollTrigger: { trigger: img, start: 'top 80%' },
      });
    });

    /* ── Split sections — text side ── */
    document.querySelectorAll('.split-section__text, .image-text__text').forEach((txt) => {
      if (!ScrollTrigger) return;
      gsap.from(Array.from(txt.children), {
        opacity: 0, y: 36,
        duration: 0.9, stagger: 0.14, ease: 'power3.out',
        scrollTrigger: { trigger: txt, start: 'top 80%' },
      });
    });

    /* ── Testimonial cards ── */
    document.querySelectorAll('.testimonials-slider').forEach((slider) => {
      if (!ScrollTrigger) return;
      gsap.from(slider, {
        opacity: 0, y: 50,
        duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: slider, start: 'top 80%' },
      });
    });

    /* ── Announcement bar text ── */
    const announcementText = document.querySelector('.announcement-bar__text');
    if (announcementText) {
      gsap.from(announcementText, { opacity: 0, y: -8, duration: 0.6, delay: 0.3, ease: 'power2.out' });
    }

    /* ── Number counter animation ── */
    document.querySelectorAll('[data-count]').forEach((el) => {
      if (!ScrollTrigger) return;
      const target = parseFloat(el.dataset.count);
      const decimals = (el.dataset.count.split('.')[1] || '').length;
      const suffix   = el.dataset.suffix || '';
      gsap.from({ val: 0 }, {
        val: target,
        duration: 1.8, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%' },
        onUpdate: function() {
          el.textContent = this.targets()[0].val.toFixed(decimals) + suffix;
        },
      });
    });

    /* ── Free shipping bar fill ── */
    const fsBars = document.querySelectorAll('.free-shipping-bar__fill');
    fsBars.forEach((bar) => {
      if (!ScrollTrigger) return;
      const w = bar.style.width;
      bar.style.width = '0%';
      gsap.to(bar, {
        width: w,
        duration: 1.2, ease: 'power2.out',
        scrollTrigger: { trigger: bar, start: 'top 90%' },
      });
    });

    /* ── Footer — stagger columns ── */
    const footer = document.querySelector('.site-footer');
    if (footer && ScrollTrigger) {
      gsap.from(footer.querySelectorAll('.footer-col, .footer__bottom'), {
        opacity: 0, y: 40,
        duration: 0.9, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: footer, start: 'top 90%' },
      });
    }

    /* ── Magnetic buttons ── */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) document.querySelectorAll('.btn-primary, .btn-secondary, .site-nav__cta').forEach((btn) => {
      btn.addEventListener('mousemove', function(e) {
        const rect   = btn.getBoundingClientRect();
        const dx     = e.clientX - (rect.left + rect.width  / 2);
        const dy     = e.clientY - (rect.top  + rect.height / 2);
        gsap.to(btn, { x: dx * 0.18, y: dy * 0.18, duration: 0.35, ease: 'power2.out' });
      });
      btn.addEventListener('mouseleave', function() {
        gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.4)' });
      });
    });

    /* ── Flavor lineup panels ── */
    document.querySelectorAll('.flavor-lineup__panel.is-active .flavor-lineup__bottle-img').forEach(function(img) {
      if (!ScrollTrigger) return;
      gsap.from(img, { opacity: 0, y: 50, scale: 0.92, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: img.closest('.flavor-lineup'), start: 'top 75%' } });
    });

    /* ── Merch grid items ── */
    staggerIn('.merch-showcase__item', { y: 40, scale: 0.93, duration: 0.7, stagger: 0.06 });

    /* ── Smooth link transition ── */
    document.querySelectorAll('a[href]:not([target="_blank"]):not([href^="#"]):not([href^="mailto"]):not([href^="tel"])').forEach((link) => {
      link.addEventListener('click', function(e) {
        if (compactMotion || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.hasAttribute('download')) return;
        const href = link.getAttribute('href');
        if (!href || href.startsWith('javascript')) return;
        const destination = new URL(href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        e.preventDefault();
        gsap.to(document.body, {
          opacity: 0, duration: 0.3, ease: 'power2.in',
          onComplete: function() { window.location.href = href; },
        });
      });
    });

    // Back-forward cache can restore a page after its exit fade.
    window.addEventListener('pageshow', function() {
      gsap.set(document.body, { opacity: 1 });
    });
  }

  /* ────────────────────────────────────────────────
     SWIPER SLIDERS
  ──────────────────────────────────────────────── */
  function initSwipers() {
    if (typeof Swiper === 'undefined') return;
    const isRTL = document.documentElement.dir === 'rtl';
    const reduceMotion = prefersReduced();

    /* Testimonials slider */
    document.querySelectorAll('.swiper-testimonials').forEach((el) => {
      el.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      var slideCount = el.querySelectorAll('.swiper-slide').length;
      new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: slideCount > 2,
        autoplay: reduceMotion ? false : { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true,
          renderBullet: (_, className) =>
            `<span class="slider-dot ${className}"></span>`,
        },
        navigation: {
          nextEl: el.closest('.testimonials-wrapper')?.querySelector('.slider-btn--next'),
          prevEl: el.closest('.testimonials-wrapper')?.querySelector('.slider-btn--prev'),
        },
        breakpoints: {
          640:  { slidesPerView: 1.4 },
          1024: { slidesPerView: 2.2 },
        },
        a11y: { prevSlideMessage: 'Previous', nextSlideMessage: 'Next' },
      });
    });

    /* Portfolio carousel (drag to scroll) */
    document.querySelectorAll('.swiper-portfolio').forEach((el) => {
      el.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      new Swiper(el, {
        slidesPerView: 'auto',
        spaceBetween: 20,
        freeMode: true,
        grabCursor: true,
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true,
        },
      });
    });

    /* Hero slider (if multiple slides) */
    document.querySelectorAll('.swiper-hero').forEach((el) => {
      el.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      var heroSlides = el.querySelectorAll('.swiper-slide').length;
      new Swiper(el, {
        loop: heroSlides > 1,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        autoplay: reduceMotion ? false : { delay: 6000, disableOnInteraction: false },
        speed: reduceMotion ? 0 : 1200,
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true,
        },
      });
    });
  }

  /* ────────────────────────────────────────────────
     PRODUCT GALLERY — thumbnail click
  ──────────────────────────────────────────────── */
  function initProductGallery() {
    const gallery = document.querySelector('.product-page__gallery');
    if (!gallery) return;

    const mainImage = gallery.querySelector('.product-page__main-image img');
    const thumbs    = gallery.querySelectorAll('.product-page__thumb');

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src    = thumb.dataset.src || thumb.querySelector('img')?.src;
        const srcset = thumb.dataset.srcset || '';
        const alt    = thumb.dataset.alt || '';

        if (mainImage && src) {
          // Fade transition
          mainImage.style.opacity = '0';
          mainImage.style.transform = 'scale(0.97)';
          mainImage.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          setTimeout(() => {
            mainImage.src = src;
            if (srcset) mainImage.srcset = srcset;
            if (alt) mainImage.alt = alt;
            mainImage.style.opacity = '1';
            mainImage.style.transform = 'scale(1)';
          }, 250);
        }

        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ────────────────────────────────────────────────
     QUANTITY SELECTOR
  ──────────────────────────────────────────────── */
  function initQtySelector() {
    document.querySelectorAll('.product-page__qty').forEach((qtyEl) => {
      const input   = qtyEl.querySelector('.product-page__qty-input');
      const btnMinus = qtyEl.querySelector('[data-qty-minus]');
      const btnPlus  = qtyEl.querySelector('[data-qty-plus]');

      if (!input) return;

      btnMinus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10);
        if (val > 1) input.value = val - 1;
      });

      btnPlus?.addEventListener('click', () => {
        input.value = parseInt(input.value, 10) + 1;
      });
    });
  }

  /* ────────────────────────────────────────────────
     ADD TO CART — AJAX
  ──────────────────────────────────────────────── */
  function initAddToCart() {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        if (!form) return;

        const variantId = form.querySelector('[name="id"]')?.value;
        const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
        if (!variantId) return;

        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = themeStrings.adding || 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty }),
          });

          if (res.ok) {
            btn.textContent = themeStrings.added || 'Added!';
            btn.style.background = 'var(--color-teal)';
            updateCartCount();
            setTimeout(() => {
              btn.textContent = originalText;
              btn.style.background = '';
              btn.disabled = false;
            }, 2000);
          } else {
            throw new Error('Failed');
          }
        } catch {
          btn.textContent = themeStrings.error || 'Error — try again';
          btn.style.background = '#c0392b';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.style.background = '';
            btn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  /* ── Update cart bubble count + dispatch free-shipping event ── */
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-hidden', cart.item_count === 0);
      });
      // Notify free shipping bar
      document.dispatchEvent(new CustomEvent('thahab:cart-updated', { detail: cart }));
    } catch { /* silent */ }
  }

  /* ────────────────────────────────────────────────
     VARIANT SELECTOR — update price + availability
  ──────────────────────────────────────────────── */
  function initVariantSelector() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;

    const variantSelect = form.querySelector('[name="id"]');
    const priceEl = document.querySelector('[data-product-price]');
    const btnAdd  = document.querySelector('[data-add-to-cart]');

    form.querySelectorAll('[data-option]').forEach((optionEl) => {
      optionEl.addEventListener('change', () => {
        // Simple approach: collect all selected option values and find variant
        const product = window.__thahab_product;
        if (!product) return;

        const selectedOptions = Array.from(
          form.querySelectorAll('[data-option]')
        ).map((el) => (el.tagName === 'SELECT' ? el.value : el.checked ? el.value : null))
         .filter(Boolean);

        const variant = product.variants.find((v) =>
          selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt)
        );

        if (!variant) return;
        if (variantSelect) variantSelect.value = variant.id;

        if (priceEl) {
          priceEl.innerHTML = formatMoney(variant.price);
        }

        if (btnAdd) {
          btnAdd.disabled = !variant.available;
          btnAdd.textContent = variant.available ? (themeStrings.addToCart || 'Add to Cart') : (themeStrings.soldOut || 'Sold Out');
        }

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      });
    });
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });
  }

  /* ────────────────────────────────────────────────
     NEWSLETTER FORM
  ──────────────────────────────────────────────── */
  function initNewsletterForms() {
    document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = form.querySelector('input[type="email"]')?.value;
        if (!email) return;

        const btn = form.querySelector('button[type="submit"], [data-submit]');
        const originalButtonText = btn?.textContent;
        if (btn) {
          btn.disabled = true;
          btn.textContent = themeStrings.subscribing || 'Subscribing…';
        }

        try {
          const res = await fetch('/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ form_type: 'customer', email, tags: 'newsletter' }),
          });
          if (!res.ok) throw new Error('Newsletter request failed');

          const msg = form.querySelector('[data-success-msg]');
          if (msg) {
            msg.textContent = themeStrings.newsletterSuccess || 'Thanks for subscribing!';
            msg.style.display = 'block';
          }
          form.reset();
        } catch {
          const msg = form.querySelector('[data-success-msg]');
          if (msg) {
            msg.textContent = themeStrings.newsletterError || 'Something went wrong. Please try again.';
            msg.style.display = 'block';
          }
        } finally {
          if (btn) {
            btn.disabled = false;
            btn.textContent = originalButtonText || themeStrings.newsletterSubmit || 'Subscribe';
          }
        }
      });
    });
  }

  /* ────────────────────────────────────────────────
     MARKETING POPUP — preserve the first impression
  ──────────────────────────────────────────────── */
  function initMarketingPopup() {
    const root = document.documentElement;
    const storageKey = 'thahab_marketing_popup_seen_v1';
    let revealTimer;

    const hasSeenPopup = () => {
      try { return window.localStorage.getItem(storageKey) === '1'; }
      catch (error) { return false; }
    };

    const rememberPopup = () => {
      try { window.localStorage.setItem(storageKey, '1'); }
      catch (error) { /* Storage can be unavailable in private browsing. */ }
    };

    const reveal = (remember = true) => {
      root.classList.add('marketing-popup-ready');
      if (remember) rememberPopup();
      if (revealTimer) window.clearTimeout(revealTimer);
    };

    // Auto-show once per browser, after visitors understand the storefront.
    if (!hasSeenPopup()) {
      revealTimer = window.setTimeout(() => reveal(true), 12000);
    }

    // The floating gift control remains available after the automatic visit.
    document.addEventListener('click', (event) => {
      if (event.target.closest('.globo-formbuilder .floating-button')) reveal(true);
    }, true);
  }

  /* ────────────────────────────────────────────────
     AGE GATE
  ──────────────────────────────────────────────── */
  function initAgeGate() {
    const gate = document.querySelector('.age-gate');
    if (!gate) return;

    // Check if already verified
    if (sessionStorage.getItem('thahab_age_verified')) {
      gate.classList.add('is-hidden');
      gate.style.display = 'none';
      return;
    }

    const confirmBtn = gate.querySelector('.age-gate__confirm');
    const denyBtn    = gate.querySelector('.age-gate__deny');

    confirmBtn?.addEventListener('click', () => {
      sessionStorage.setItem('thahab_age_verified', '1');
      gate.classList.add('is-hidden');
      setTimeout(() => { gate.style.display = 'none'; }, 500);
    });

    denyBtn?.addEventListener('click', () => {
      window.location.href = 'https://www.google.com';
    });
  }

  /* ────────────────────────────────────────────────
     ANNOUNCEMENT BAR — dismiss
  ──────────────────────────────────────────────── */
  function initAnnouncementBar() {
    const closeBtn = document.querySelector('[data-announcement-close]');
    const bar      = document.querySelector('.announcement-bar');
    if (!closeBtn || !bar) return;

    if (sessionStorage.getItem('thahab_announcement_dismissed')) {
      bar.style.display = 'none';
      return;
    }

    closeBtn.addEventListener('click', () => {
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
      bar.style.opacity = '0';
      bar.style.maxHeight = '0';
      bar.style.overflow = 'hidden';
      sessionStorage.setItem('thahab_announcement_dismissed', '1');
      setTimeout(updateNavHeight, 350);
    });
  }

  /* ────────────────────────────────────────────────
     COUNTER ANIMATION (for number stats)
  ──────────────────────────────────────────────── */
  function initCounters() {
    const counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el     = entry.target;
          const target = parseFloat(el.dataset.counter);
          const duration = 2000;
          const suffix   = el.dataset.suffix || '';
          const start    = performance.now();

          function update(now) {
            const progress = Math.min((now - start) / duration, 1);
            const ease     = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.floor(ease * target).toLocaleString() + suffix;
            if (progress < 1) requestAnimationFrame(update);
            else el.textContent = target.toLocaleString() + suffix;
          }

          requestAnimationFrame(update);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* ────────────────────────────────────────────────
     IMAGE HOVER SWAP (product cards)
     Preloads hover images for instant swap
  ──────────────────────────────────────────────── */
  function initImageHoverSwap() {
    document.querySelectorAll('.product-card__img--hover').forEach((img) => {
      const src = img.dataset.src || img.src;
      if (!src || src === window.location.href) return;
      const preload = new Image();
      preload.src = src;
    });
  }

  /* ────────────────────────────────────────────────
     SMOOTH ANCHOR SCROLL
  ──────────────────────────────────────────────── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const id = anchor.getAttribute('href').slice(1);
        const target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ────────────────────────────────────────────────
     STICKY HEADER HEIGHT — CSS var update
  ──────────────────────────────────────────────── */
  /* Predictive search — localized Shopify suggestions */
  function initPredictiveSearch() {
    const instances = document.querySelectorAll('[data-predictive-search="true"]');
    if (!instances.length) return;

    const cache = new Map();

    instances.forEach((root, instanceIndex) => {
      const form = root.querySelector('[data-predictive-form]');
      const input = root.querySelector('[data-predictive-input]');
      const results = root.querySelector('[data-predictive-results]');
      const status = root.querySelector('[data-predictive-status]');
      const clear = root.querySelector('[data-predictive-clear]');
      const minimumLength = Number(root.dataset.minLength || 2);
      let debounceTimer = 0;
      let requestController = null;
      let activeIndex = -1;

      if (!form || !input || !results) return;

      function options() {
        return Array.from(results.querySelectorAll('[data-predictive-option]'));
      }

      function setLoading(loading) {
        root.classList.toggle('is-loading', loading);
        input.setAttribute('aria-busy', String(loading));
        if (loading && status) status.textContent = root.dataset.loadingText || '';
      }

      function closeResults() {
        root.classList.remove('is-open');
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
        options().forEach((option) => {
          option.classList.remove('is-active');
          option.setAttribute('aria-selected', 'false');
        });
      }

      function openResults() {
        if (!results.firstElementChild) return;
        root.classList.add('is-open');
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }

      function setActive(nextIndex) {
        const items = options();
        if (!items.length) return;
        activeIndex = (nextIndex + items.length) % items.length;
        items.forEach((option, index) => {
          const active = index === activeIndex;
          option.classList.toggle('is-active', active);
          option.setAttribute('aria-selected', String(active));
          if (!option.id) option.id = 'PredictiveOption-' + instanceIndex + '-' + index;
        });
        input.setAttribute('aria-activedescendant', items[activeIndex].id);
        items[activeIndex].scrollIntoView({ block: 'nearest' });
      }

      function localizeResultLabels(container, term) {
        const labels = [
          ['.predictive-search-results__heading--queries', root.dataset.suggestedQueriesLabel],
          ['.predictive-search-results__heading--products', root.dataset.productsLabel],
          ['.predictive-search-results__heading--collections', root.dataset.collectionsLabel],
          ['.predictive-search-results__heading--pages', root.dataset.pagesLabel],
          ['.predictive-search-results__heading--articles', root.dataset.articlesLabel]
        ];
        labels.forEach(([selector, label]) => {
          const element = container.querySelector(selector);
          if (element && label) element.textContent = label;
        });

        const viewAll = container.querySelector('.predictive-search-results__all > span:first-child');
        if (viewAll && root.dataset.viewAllLabel) {
          viewAll.textContent = root.dataset.viewAllLabel + ' “' + term + '”';
        }

        const empty = container.querySelector('[data-predictive-empty]');
        if (empty) {
          const message = empty.querySelector('p');
          const link = empty.querySelector('a');
          if (message && root.dataset.noResultsText) message.textContent = root.dataset.noResultsText;
          if (link && root.dataset.searchForLabel) link.textContent = root.dataset.searchForLabel + ' “' + term + '”';
        }
      }

      function render(markup, term) {
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const renderedResults = parsed.querySelector('.predictive-search-results');
        if (!renderedResults) {
          closeResults();
          return;
        }

        results.innerHTML = renderedResults.outerHTML;
        localizeResultLabels(results, term);
        const items = options();
        items.forEach((option, index) => {
          option.id = 'PredictiveOption-' + instanceIndex + '-' + index;
          option.setAttribute('aria-selected', 'false');
        });
        activeIndex = -1;
        if (status) {
          const count = Number(renderedResults.dataset.resultCount || items.length);
          status.textContent = count
            ? count + ' ' + (root.dataset.resultsText || '')
            : (root.dataset.noResultsText || '');
        }
        openResults();
      }

      async function getResults(term) {
        const normalizedTerm = term.trim();
        if (normalizedTerm.length < minimumLength) {
          if (requestController) requestController.abort();
          setLoading(false);
          closeResults();
          return;
        }

        if (requestController) requestController.abort();

        const cacheKey = document.documentElement.lang + ':' + normalizedTerm.toLocaleLowerCase();
        if (cache.has(cacheKey)) {
          setLoading(false);
          render(cache.get(cacheKey), normalizedTerm);
          return;
        }

        const controller = new AbortController();
        requestController = controller;
        setLoading(true);

        const locale = (document.documentElement.lang || '').toLowerCase();
        const endpoint = locale.startsWith('ar') ? '/search/suggest' : root.dataset.predictiveUrl;
        const url = new URL(endpoint || '/search/suggest', window.location.origin);
        url.searchParams.set('q', normalizedTerm);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'query,product,collection,page,article');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        url.searchParams.set('resources[options][fields]', 'title,product_type,tag,variants.title,vendor');

        try {
          const response = await fetch(url.toString(), {
            signal: controller.signal,
            headers: { Accept: 'text/html' },
            credentials: 'same-origin'
          });
          if (!response.ok) throw new Error('Predictive search request failed');
          const markup = await response.text();
          cache.set(cacheKey, markup);
          if (input.value.trim() === normalizedTerm) render(markup, normalizedTerm);
        } catch (error) {
          if (error.name !== 'AbortError') closeResults();
        } finally {
          if (requestController === controller) setLoading(false);
        }
      }

      input.addEventListener('input', () => {
        if (clear) clear.hidden = input.value.length === 0;
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => getResults(input.value), 220);
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= minimumLength && results.firstElementChild) openResults();
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openResults();
          setActive(activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          openResults();
          setActive(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          const selected = options()[activeIndex];
          if (selected) {
            event.preventDefault();
            window.location.assign(selected.href);
          }
        } else if (event.key === 'Escape') {
          closeResults();
        }
      });

      if (clear) {
        clear.addEventListener('click', () => {
          input.value = '';
          clear.hidden = true;
          closeResults();
          input.focus();
        });
        clear.hidden = input.value.length === 0;
      }

      form.addEventListener('submit', (event) => {
        if (!input.value.trim()) event.preventDefault();
      });

      document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) closeResults();
      });
    });
  }

  function updateNavHeight() {
    const headerGroup = document.querySelector('.shopify-section-group-header-group') || document.querySelector('.site-nav');
    if (headerGroup) {
      document.documentElement.style.setProperty('--nav-height', headerGroup.offsetHeight + 'px');
    }
  }

  /* ────────────────────────────────────────────────
     INIT ON DOM READY
  ──────────────────────────────────────────────── */
  function init() {
    updateNavHeight();
    initNav();
    initPredictiveSearch();
    initScrollReveal();
    initAgeGate();
    initAnnouncementBar();
    initProductGallery();
    initQtySelector();
    initAddToCart();
    initVariantSelector();
    initNewsletterForms();
    initMarketingPopup();
    initCounters();
    initImageHoverSwap();
    initSmoothScroll();
    initWishlist();
    initQuickView();
    initRecentlyViewed();
    initBottleAttributes();
    initBackInStock();

    // GSAP — only after gsap is guaranteed loaded
    if (typeof gsap !== 'undefined') {
      initGSAP();
    } else {
      window.addEventListener('gsap:ready', initGSAP);
    }

    // Swiper — only after Swiper is guaranteed loaded
    if (typeof Swiper !== 'undefined') {
      initSwipers();
    } else {
      window.addEventListener('swiper:ready', initSwipers);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose updateCartCount globally for Shopify section scripts */
  window.ThahabTheme = { updateCartCount };
})();

/* ═══════════════════════════════════════════════════
   WISHLIST — localStorage + Shopify Customer Metafield sync
   For logged-in customers: syncs to customer metafield
   custom.wishlist (JSON list). For guests: localStorage only.
═══════════════════════════════════════════════════ */
function initWishlist() {
  var STORAGE_KEY = 'thahab_wishlist';
  var customer    = window.__thahab_customer || {};

  /* ── Seed from server (logged-in customer metafield) ── */
  if (customer.loggedIn && Array.isArray(customer.wishlist) && customer.wishlist.length > 0) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(customer.wishlist));
  }

  function getList() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch(e) { return []; }
  }

  function saveList(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    if (customer.loggedIn) syncToServer(list);
  }

  function inList(id) {
    return getList().some(function(p) { return String(p.id) === String(id); });
  }

  /* ── Sync to Shopify customer metafield via /account ── */
  function syncToServer(list) {
    var token = document.getElementById('WishlistSyncToken');
    if (!token) return;
    var body = new URLSearchParams();
    body.append('_method', 'patch');
    body.append('authenticity_token', token.value);
    body.append('customer[metafields][custom.wishlist]', JSON.stringify(list));
    fetch('/account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      credentials: 'same-origin'
    }).catch(function() { /* silent — localStorage is always up to date */ });
  }

  function syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function(btn) {
      var wishlisted = inList(btn.dataset.productId);
      btn.classList.toggle('is-wishlisted', wishlisted);
      btn.setAttribute('aria-pressed', wishlisted ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    var id   = btn.dataset.productId;
    var list = getList();
    var idx  = list.findIndex(function(p) { return String(p.id) === String(id); });
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      list.push({
        id:    id,
        title: btn.dataset.productTitle,
        url:   btn.dataset.productUrl,
        image: btn.dataset.productImage,
        price: btn.dataset.productPrice
      });
    }
    saveList(list);
    syncButtons();

    /* Show login prompt for guests */
    if (!customer.loggedIn) {
      showWishlistLoginHint(btn);
    }
  });

  /* Prompt guests to log in to save permanently */
  function showWishlistLoginHint(btn) {
    var existing = document.getElementById('WishlistLoginHint');
    if (existing) existing.remove();
    var hint = document.createElement('div');
    hint.id = 'WishlistLoginHint';
    hint.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#0A4A52;color:#fff;padding:12px 20px;border-radius:100px;font-size:0.78rem;font-weight:700;z-index:9999;box-shadow:0 8px 32px rgba(10,74,82,0.25);white-space:nowrap;';
    hint.innerHTML = 'Saved locally. <a href="/account/login" style="color:var(--color-brand);text-decoration:underline;">Log in</a> to save across devices.';
    document.body.appendChild(hint);
    setTimeout(function() { if (hint.parentNode) hint.remove(); }, 4000);
  }

  /* ── Render wishlist page ── */
  var grid  = document.getElementById('WishlistGrid');
  var empty = document.getElementById('WishlistEmpty');
  if (grid) {
    var list = getList();
    if (list.length === 0) {
      if (empty) empty.style.display = 'block';
    } else {
      list.forEach(function(p) {
        var imgSrc = p.image || '';
        if (imgSrc && imgSrc.startsWith('//')) imgSrc = 'https:' + imgSrc;
        var card = document.createElement('div');
        card.className = 'product-card';
        card.innerHTML =
          '<a href="' + p.url + '" class="product-card__images" style="display:block;position:relative;aspect-ratio:3/4;overflow:hidden;background:var(--color-surface-2);">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + p.title + '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;">' : '') +
          '</a>' +
          '<div class="product-card__body">' +
            '<h3 class="product-card__title"><a href="' + p.url + '">' + p.title + '</a></h3>' +
            '<div class="product-card__price">' + p.price + '</div>' +
            '<a href="' + p.url + '" class="product-card__btn" style="display:flex;align-items:center;justify-content:center;margin-top:14px;height:42px;background:var(--color-fg);color:#fff;border-radius:100px;font-size:0.65rem;font-weight:900;letter-spacing:0.12em;text-transform:uppercase;">View Product</a>' +
          '</div>';
        grid.appendChild(card);
      });
    }
  }

  syncButtons();
}

/* ═══════════════════════════════════════════════════
   QUICK VIEW MODAL
═══════════════════════════════════════════════════ */
function initQuickView() {
  var modal   = document.getElementById('QuickViewModal');
  var content = document.getElementById('QuickViewContent');
  if (!modal) return;
  var strings = window.__thahab_strings || {};

  var currentProduct = null;
  var selectedVariantId = null;
  var previouslyFocused = null;
  var previousBodyOverflow = '';

  function openModal() {
    previouslyFocused = document.activeElement;
    previousBodyOverflow = document.body.style.overflow;
    modal.style.display = 'flex';
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    content.scrollTop = 0;
    window.requestAnimationFrame(function() {
      var closeBtn = modal.querySelector('.quickview-modal__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    });
  }
  function closeModal() {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = previousBodyOverflow;
    currentProduct = null;
    content.innerHTML = '<div class="quickview-modal__loading"><div class="quickview-modal__spinner"></div></div>';
    if (previouslyFocused && document.contains(previouslyFocused)) {
      previouslyFocused.focus({ preventScroll: true });
    }
    previouslyFocused = null;
  }

  function moneyFormat(cents) {
    var locale = document.documentElement.lang || 'en';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SAR';
    return (cents / 100).toLocaleString(locale, { style: 'currency', currency: currency });
  }

  function findVariant(product, selections) {
    return product.variants.find(function(v) {
      return v.options.every(function(opt, i) { return opt === selections[i]; });
    });
  }

  function renderModal(product) {
    currentProduct = product;
    var firstVariant = product.variants[0];
    selectedVariantId = firstVariant.id;

    // Build option selectors
    var optionsHTML = '';
    if (!product.has_only_default_variant) {
      product.options.forEach(function(optName, optIdx) {
        var values = product.variants.map(function(v) { return v.options[optIdx]; })
          .filter(function(v, i, arr) { return arr.indexOf(v) === i; });
        optionsHTML += '<div style="margin-bottom:16px;">';
        optionsHTML += '<div style="font-size:0.72rem;font-weight:900;letter-spacing:0.15em;text-transform:uppercase;margin-bottom:8px;">' + optName + '</div>';
        optionsHTML += '<div style="display:flex;flex-wrap:wrap;gap:8px;" data-option-group="' + optIdx + '">';
        values.forEach(function(val, vi) {
          var isFirst = vi === 0;
          optionsHTML += '<button type="button" class="qv-option' + (isFirst ? ' is-selected' : '') + '" data-option-idx="' + optIdx + '" data-option-val="' + val + '" style="height:36px;padding:0 14px;border-radius:100px;border:2px solid ' + (isFirst ? 'var(--color-brand)' : 'var(--color-border)') + ';font-size:0.8rem;font-weight:700;cursor:pointer;background:' + (isFirst ? 'rgba(166,28,34,0.05)' : 'transparent') + ';color:' + (isFirst ? 'var(--color-brand)' : 'var(--color-fg)') + ';" >' + val + '</button>';
        });
        optionsHTML += '</div></div>';
      });
    }

    // Price display
    var priceHTML = firstVariant.compare_at_price > firstVariant.price
      ? '<span class="price-compare">' + moneyFormat(firstVariant.compare_at_price) + '</span> <span class="price-sale">' + moneyFormat(firstVariant.price) + '</span>'
      : '<span class="price-regular">' + moneyFormat(firstVariant.price) + '</span>';

    // /products/[handle].js returns featured_image as a string URL, not an object
    var imgSrc = typeof product.featured_image === 'string'
      ? product.featured_image
      : (product.featured_image && product.featured_image.src ? product.featured_image.src : '');
    if (imgSrc && imgSrc.startsWith('//')) imgSrc = 'https:' + imgSrc;

    content.innerHTML =
      '<div class="quickview-modal__grid">' +
        '<div class="quickview-modal__image">' +
          (imgSrc ? '<img id="QvImg" src="' + imgSrc + '" alt="' + product.title + '">' : '') +
        '</div>' +
        '<div class="quickview-modal__info">' +
          (product.vendor ? '<div style="font-size:0.65rem;font-weight:900;letter-spacing:0.2em;text-transform:uppercase;color:rgba(166,28,34,0.65);margin-bottom:8px;">' + product.vendor + '</div>' : '') +
          '<div class="quickview-modal__title">' + product.title + '</div>' +
          '<div class="quickview-modal__price" id="QvPrice" style="margin:12px 0 20px;">' + priceHTML + '</div>' +
          optionsHTML +
          '<div class="quickview-modal__actions">' +
            '<button id="QvAddToCart" type="button" class="btn btn-primary" style="flex:1;" data-variant-id="' + firstVariant.id + '">' +
              (firstVariant.available ? (strings.addToCart || 'Add to Cart') : (strings.soldOut || 'Sold Out')) +
            '</button>' +
            '<a href="/products/' + product.handle + '" class="btn btn-secondary" style="white-space:nowrap;">' + (strings.viewDetails || 'View details') + '</a>' +
          '</div>' +
        '</div>' +
      '</div>';

    // Wire up option selectors
    content.querySelectorAll('.qv-option').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var optIdx = parseInt(this.dataset.optionIdx);
        var optVal = this.dataset.optionVal;

        // Update selected state in this group
        content.querySelectorAll('[data-option-idx="' + optIdx + '"]').forEach(function(b) {
          b.classList.remove('is-selected');
          b.style.border = '2px solid var(--color-border)';
          b.style.background = 'transparent';
          b.style.color = 'var(--color-fg)';
        });
        this.classList.add('is-selected');
        this.style.border = '2px solid var(--color-brand)';
        this.style.background = 'rgba(166,28,34,0.05)';
        this.style.color = 'var(--color-brand)';

        // Recalculate selected variant
        var selections = [];
        for (var i = 0; i < product.options.length; i++) {
          var sel = content.querySelector('[data-option-idx="' + i + '"].is-selected');
          selections.push(sel ? sel.dataset.optionVal : product.variants[0].options[i]);
        }
        var variant = findVariant(product, selections);
        if (!variant) return;
        selectedVariantId = variant.id;

        // Update price
        var priceEl = document.getElementById('QvPrice');
        if (priceEl) {
          priceEl.innerHTML = variant.compare_at_price > variant.price
            ? '<span class="price-compare">' + moneyFormat(variant.compare_at_price) + '</span> <span class="price-sale">' + moneyFormat(variant.price) + '</span>'
            : '<span class="price-regular">' + moneyFormat(variant.price) + '</span>';
        }

        // Update ATC button
        var atcBtn = document.getElementById('QvAddToCart');
        if (atcBtn) {
          atcBtn.dataset.variantId = variant.id;
          atcBtn.textContent = variant.available ? (strings.addToCart || 'Add to Cart') : (strings.soldOut || 'Sold Out');
          atcBtn.disabled = !variant.available;
        }

        // Update image if variant has one
        if (variant.featured_image) {
          var imgEl = document.getElementById('QvImg');
          if (imgEl) {
            var src = typeof variant.featured_image === 'string'
              ? variant.featured_image
              : (variant.featured_image.src || '');
            if (src && src.startsWith('//')) src = 'https:' + src;
            if (src) imgEl.src = src;
          }
        }
      });
    });

    // Wire up ATC button
    var atcBtn = document.getElementById('QvAddToCart');
    if (atcBtn) {
      atcBtn.addEventListener('click', function() {
        if (!selectedVariantId) return;
        var btn = this;
        btn.disabled = true;
        var prev = btn.textContent;
        btn.textContent = strings.adding || 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(btn.dataset.variantId), quantity: 1 })
        }).then(function(r) {
          if (r.ok) {
            btn.textContent = (strings.added || 'Added!') + ' ✓';
            btn.style.background = 'var(--color-teal)';
            if (window.ThahabTheme) window.ThahabTheme.updateCartCount();
            setTimeout(function() { closeModal(); }, 1200);
          } else {
            btn.textContent = strings.error || 'Try again';
          }
        }).catch(function() {
          btn.textContent = strings.error || 'Error';
        }).finally(function() {
          setTimeout(function() {
            btn.disabled = false;
            btn.textContent = prev;
            btn.style.background = '';
          }, 2000);
        });
      });
    }
  }

  // Open on quick view button click — fetch product JSON
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-quickview-btn]');
    if (!btn) return;
    e.preventDefault();
    openModal();
    fetch('/products/' + btn.dataset.handle + '.js')
      .then(function(r) { return r.json(); })
      .then(function(product) { renderModal(product); })
      .catch(function() {
        var message = strings.couldNotLoad || 'Could not load product.';
        content.innerHTML = '<div style="padding:40px;text-align:center;color:rgba(10,74,82,0.4);"></div>';
        content.firstElementChild.textContent = message;
      });
  });

  // Close
  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-quickview-close]')) closeModal();
  });
  document.addEventListener('keydown', function(e) {
    if (modal.style.display === 'none') return;
    if (e.key === 'Escape') {
      closeModal();
      return;
    }
    if (e.key !== 'Tab') return;

    var focusable = Array.from(modal.querySelectorAll(
      'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function(el) { return el.offsetParent !== null; });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
}

/* ═══════════════════════════════════════════════════
   RECENTLY VIEWED — localStorage
═══════════════════════════════════════════════════ */
function initRecentlyViewed() {
  var STORAGE_KEY = 'thahab_recently_viewed';
  var MAX = 8;

  function getList() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch(e) { return []; }
  }

  // Record current product page
  if (window.__thahab_product) {
    var p    = window.__thahab_product;
    var list = getList();
    list = list.filter(function(item) { return item.id !== p.id; });
    list.unshift({
      id:    p.id,
      title: p.title,
      url:   p.url,
      image: p.featured_image ? (p.featured_image.src.startsWith('//') ? 'https:' + p.featured_image.src : p.featured_image.src) : '',
      price: p.price
    });
    if (list.length > MAX) list = list.slice(0, MAX);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  // Render recently viewed section
  var grid    = document.getElementById('RecentlyViewedGrid');
  var section = document.getElementById('RecentlyViewed');
  if (!grid || !section) return;

  var list = getList();
  if (window.__thahab_product) {
    list = list.filter(function(item) { return item.id !== window.__thahab_product.id; });
  }
  if (list.length === 0) return;

  section.style.display = 'block';
  list.slice(0, 4).forEach(function(p) {
    var price = typeof p.price === 'number' ? (p.price / 100).toFixed(2) : p.price;
    var imgSrc = p.image || '';
    if (imgSrc && imgSrc.startsWith('//')) imgSrc = 'https:' + imgSrc;
    var card  = document.createElement('div');
    card.className = 'product-card';
    card.innerHTML =
      '<a href="' + p.url + '" class="product-card__images" style="display:block;position:relative;aspect-ratio:3/4;overflow:hidden;background:var(--color-surface-2);">' +
        (imgSrc ? '<img src="' + imgSrc + '" alt="' + p.title + '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;">' : '') +
      '</a>' +
      '<div class="product-card__body">' +
        '<h3 class="product-card__title"><a href="' + p.url + '">' + p.title + '</a></h3>' +
        '<div class="product-card__price">SAR ' + price + '</div>' +
      '</div>';
    grid.appendChild(card);
  });
}

/* ═══════════════════════════════════════════════════
   BOTTLE ATTRIBUTES — scroll-driven panel reveal
═══════════════════════════════════════════════════ */
function initBottleAttributes() {
  var section  = document.getElementById('BottleAttributes');
  var panels   = document.querySelectorAll('#BottlePanels .bottle-attr-panel, #BottlePanelsRight .bottle-attr-panel');
  var bottleEl = document.getElementById('BottleImg');
  if (!section || !panels.length) return;

  var panelPairs = document.querySelectorAll('#BottlePanels .bottle-attr-panel');
  var total      = panelPairs.length;

  function onScroll() {
    var rect     = section.getBoundingClientRect();
    var progress = Math.max(0, Math.min(1, -rect.top / (rect.height - window.innerHeight)));
    var active   = Math.floor(progress * total);

    panels.forEach(function(p, i) {
      var pairIdx = i % total;
      p.classList.toggle('is-active', pairIdx === active);
    });

    if (bottleEl) {
      var rotation = progress * 360;
      bottleEl.style.transform = 'rotate(' + rotation + 'deg) translateY(' + (Math.sin(progress * Math.PI * 2) * 10) + 'px)';
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ═══════════════════════════════════════════════════
   BACK-IN-STOCK — email capture
═══════════════════════════════════════════════════ */
function initBackInStock() {
  document.querySelectorAll('[data-back-in-stock]').forEach(function(form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var email    = form.querySelector('[name="email"]').value;
      var success  = form.querySelector('.back-in-stock__success');
      var btn      = form.querySelector('button[type="submit"]');
      if (!email) return;

      btn.disabled    = true;
      btn.textContent = '…';

      // Shopify doesn't have a native back-in-stock API on basic plans.
      // Store locally + show confirmation (connect to Klaviyo/Mailchimp webhook if available)
      var stored = JSON.parse(localStorage.getItem('thahab_bis') || '[]');
      stored.push({ email: email, product: form.querySelector('[name="product_id"]').value, variant: form.querySelector('[name="variant_id"]').value, date: new Date().toISOString() });
      localStorage.setItem('thahab_bis', JSON.stringify(stored));

      if (success) success.style.display = 'block';
      form.querySelector('input[type="email"]').value = '';
      btn.disabled    = false;
      btn.textContent = themeStrings.notifyMe || 'Notify Me';
    });
  });
}
